import { useCallback, useEffect, useState } from "react";
import {
  archiveCoachClientPlan,
  createCoachClientPlan,
  fetchCoachClientPlans,
} from "../lib/stripe.js";
import { useAuthUserId } from "./useAuthUserId.js";

export function useCoachClientPlans(opts = {}) {
  const { userId, authLoading } = useAuthUserId();
  const [plans, setPlans] = useState([]);
  const [coachId, setCoachId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mutating, setMutating] = useState(false);

  const refetch = useCallback(async () => {
    if (!userId) {
      setPlans([]);
      setCoachId(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchCoachClientPlans({
        includeArchived: opts.includeArchived,
        coachId: opts.coachId,
      });
      setPlans(data.plans ?? []);
      setCoachId(data.coachId ?? null);
    } catch (e) {
      setError(e?.message ?? String(e));
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId, opts.includeArchived, opts.coachId]);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      setPlans([]);
      setCoachId(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    refetch();
  }, [userId, authLoading, refetch]);

  async function createPlan(fields) {
    setMutating(true);
    setError(null);
    try {
      await createCoachClientPlan(fields);
      await refetch();
    } catch (e) {
      setError(e?.message ?? String(e));
      throw e;
    } finally {
      setMutating(false);
    }
  }

  async function archivePlan(planId) {
    setMutating(true);
    setError(null);
    try {
      await archiveCoachClientPlan(planId);
      await refetch();
    } catch (e) {
      setError(e?.message ?? String(e));
      throw e;
    } finally {
      setMutating(false);
    }
  }

  return { plans, coachId, isLoading, error, mutating, refetch, createPlan, archivePlan };
}
